import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Field, Formik } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import styled from 'styled-components';

import { StyledForm } from '../../../Hoc/elements';
import Button from '../../../Components/UI/Forms/Button/Button';
import Input from '../../../Components/UI/Forms/Inputs/Input';
import Message from '../../../Components/UI/Messages/Message';
import AddressLocationPicker from '../../../Components/Geo/AddressLocationPicker';
import Modal from '../../../Components/UI/Modal/Modal';
import ProfilePhotoEditor from '../../../Components/Profile/ProfilePhotoEditor';
import ProfileSecurityCard from '../../../Components/Profile/ProfileSecurityCard';
import ProfileLanguageSettings from '../../../Components/I18n/ProfileLanguageSettings';
import ArtisanDocumentTemplateCard from '../../../Components/Profile/ArtisanDocumentTemplateCard';
import StripeConnectPanel from '../../../Components/Payments/StripeConnectPanel';
import ArtisanCommandCenter from '../../../Components/Dashboard/ArtisanCommandCenter';
import InvoicingSetupCard from '../../../Components/Invoicing/InvoicingSetupCard';
import ReputationSection from '../../../Components/Trust/ReputationSection';
import * as actions from '../../../Store/Actions';
import { artisanRegistrationLabel } from '../../../Utils/artisanStatus';
import { normalizeRegionCode, normalizeRegionList, regionOptions } from '../../../Config/franceRegions';
import { toCoordinate } from '../../../Utils/geo';
import {
  ActionBar,
  DangerCard,
  FieldGrid,
  FormStack,
  Full,
  ProfileGrid,
  ProfileHeader,
  ProfilePage,
  SectionCard,
  SectionHeader,
} from '../../../Components/Profile/ProfilePageShell';
import {
  DeleteButton,
  Label,
  ModalActions,
  ModalContent,
  PasswordHint,
  SaveState,
  Select,
  SelectGroup,
  TextArea,
} from './ProfileFormLayout';

const phoneRegExp = /^(?:(?:\+|00)33|0)[1-9](?:[ .-]?\d{2}){4}$/;

const ProfileSchema = Yup.object({
  firstName: Yup.string().trim().required('Votre prénom est requis.').min(2, 'Trop court.').max(40, 'Trop long.'),
  lastName: Yup.string().trim().required('Votre nom est requis.').min(2, 'Trop court.').max(50, 'Trop long.'),
  email: Yup.string().trim().email('Adresse e-mail invalide.').required("L’adresse e-mail est requise."),
  contactNumber: Yup.string().trim().test('phone-format', 'Utilisez un numéro français valide.', (value) => !String(value || '').trim() || phoneRegExp.test(String(value).trim())),
  address: Yup.string().trim().max(180, 'Adresse trop longue.'),
  specialityType: Yup.string(),
  region: Yup.string(),
  businessIntroduction: Yup.string().trim().max(600, 'Votre présentation ne peut pas dépasser 600 caractères.'),
  certifications: Yup.string().trim().max(500, 'La liste des certifications est trop longue.'),
  businessName: Yup.string().trim().max(100, 'Le nom commercial est trop long.'),
  legalForm: Yup.string().trim().max(80, 'La forme juridique est trop longue.'),
  siret: Yup.string().trim().matches(/^(?:\d{14})?$/, 'Le SIRET doit contenir 14 chiffres.'),
  vatNumber: Yup.string().trim().max(30, 'Le numéro de TVA est trop long.'),
  professionalInsurance: Yup.string().trim().max(220, 'La référence d’assurance est trop longue.'),
  companyWebsite: Yup.string().trim().max(180, 'L’adresse du site est trop longue.').test('website', 'Adresse web invalide.', (value) => !value || /^https?:\/\//i.test(value)),
  quoteValidityDays: Yup.number().min(1, 'Minimum 1 jour.').max(365, 'Maximum 365 jours.'),
  paymentTerms: Yup.string().trim().max(500, 'Les conditions de paiement sont trop longues.'),
  documentFooter: Yup.string().trim().max(240, 'Le message de bas de page est trop long.'),
  serviceAddress: Yup.string().trim().max(180, 'Adresse trop longue.'),
  serviceRadius: Yup.number().min(5, 'Rayon minimum : 5 km.').max(150, 'Rayon maximum : 150 km.'),
});


const PROFILE_FIELD_LABELS = {
  firstName: 'prénom',
  lastName: 'nom',
  email: 'adresse e-mail',
  contactNumber: 'téléphone professionnel',
  address: 'adresse professionnelle',
  specialityType: 'spécialité',
  region: 'région principale',
  businessIntroduction: 'présentation de l’entreprise',
  certifications: 'certifications',
  businessName: 'nom commercial',
  legalForm: 'forme juridique',
  siret: 'SIRET',
  vatNumber: 'numéro de TVA',
  professionalInsurance: 'assurance professionnelle',
  companyWebsite: 'site web',
  quoteValidityDays: 'validité des devis',
  paymentTerms: 'conditions de paiement',
  documentFooter: 'message de bas de page',
  serviceAddress: 'point de départ professionnel',
  serviceRadius: 'rayon d’intervention',
};

const touchedFields = (fieldNames) => fieldNames.reduce((result, fieldName) => {
  result[fieldName] = true;
  return result;
}, {});

const ProfileArtisan = ({ firebase, editProfile, loading, error, loadingDelete, errorDelete, deleteUser, cleanUp }) => {
  const [modalOpened, setModalOpened] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');

  useEffect(() => () => cleanUp(), [cleanUp]);

  if (!firebase.profile.isLoaded) return null;

  const registrationLabel = artisanRegistrationLabel(firebase.profile.registration_status);

  return (
    <>
      <Formik
        enableReinitialize
        validateOnMount
        initialValues={{
          firstName: firebase.profile.firstName || '',
          lastName: firebase.profile.lastName || '',
          email: firebase.auth.email || '',
          contactNumber: String(firebase.profile.phone_number || ''),
          address: firebase.profile.address || '',
          specialityType: firebase.profile.speciality || '',
          region: normalizeRegionCode(firebase.profile.artisan_region || ''),
          serviceRegions: normalizeRegionList([
            normalizeRegionCode(firebase.profile.artisan_region || ''),
            ...(Array.isArray(firebase.profile.service_regions) ? firebase.profile.service_regions : []),
          ]),
          acceptsOutsideRegions: firebase.profile.accepts_outside_regions === true,
          nationwideService: Boolean(firebase.profile.nationwide_service),
          registrationStatus: firebase.profile.registration_status || 'pending',
          profileImage: firebase.profile.profile_image || '',
          totalNumJobs: firebase.profile.total_num_jobs || 0,
          rating: firebase.profile.artisan_rating || 0,
          businessIntroduction: firebase.profile.business_intro || '',
          certifications: Array.isArray(firebase.profile.certifications)
            ? firebase.profile.certifications.join('\n')
            : firebase.profile.certifications || '',
          serviceAddress: firebase.profile.service_area?.base_address || '',
          serviceLocation: firebase.profile.service_area?.center || null,
          serviceRadius: firebase.profile.service_area?.radius_km || 30,
          geoNotifications: Boolean(firebase.profile.service_area?.center) && firebase.profile.geo_notifications?.enabled !== false,
          availabilityStatus: firebase.profile.availability_status || 'available',
          businessName: firebase.profile.document_template?.business_name || firebase.profile.business_name || firebase.profile.company_name || '',
          legalForm: firebase.profile.document_template?.legal_form || '',
          siret: firebase.profile.document_template?.siret || firebase.profile.siret || '',
          vatNumber: firebase.profile.document_template?.vat_number || '',
          professionalInsurance: firebase.profile.document_template?.professional_insurance || firebase.profile.professional_insurance || firebase.profile.insurance || '',
          companyWebsite: firebase.profile.document_template?.website || '',
          companyLogo: firebase.profile.document_template?.logo_url || '',
          quoteValidityDays: firebase.profile.document_template?.quote_validity_days || 30,
          defaultTaxMode: firebase.profile.document_template?.default_tax_mode || '',
          paymentTerms: firebase.profile.document_template?.payment_terms || 'Acompte de 20 % à l’acceptation du devis. Solde après confirmation de la fin des travaux par le client.',
          documentFooter: firebase.profile.document_template?.footer_note || '',
        }}
        validationSchema={ProfileSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          const normalizedValues = {
            ...values,
            geoNotifications: Boolean(toCoordinate(values.serviceLocation) && values.geoNotifications),
          };
          const updated = await editProfile(normalizedValues);
          if (updated) {
            resetForm({ values: normalizedValues });
            setSavedAt(new Date());
            toast.success('Profil artisan enregistré.');
          }
          setSubmitting(false);
        }}
      >
        {({
          dirty,
          errors,
          isSubmitting,
          isValid,
          values,
          handleChange,
          setFieldValue,
          setTouched,
          submitForm,
          validateForm,
        }) => {
          const hasServiceLocation = Boolean(toCoordinate(values.serviceLocation));
          const invalidFieldNames = Object.keys(errors).filter((fieldName) => PROFILE_FIELD_LABELS[fieldName]);
          const invalidFieldLabels = invalidFieldNames.map((fieldName) => PROFILE_FIELD_LABELS[fieldName]);

          const handleSave = async () => {
            const validationErrors = await validateForm();
            const fieldNames = Object.keys(validationErrors);

            if (fieldNames.length) {
              await setTouched(touchedFields(fieldNames), true);
              const firstField = fieldNames[0];
              window.requestAnimationFrame(() => {
                const element = document.querySelector(`[name="${firstField}"]`)
                  || document.getElementById(firstField);
                element?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
                element?.focus?.({ preventScroll: true });
              });
              return;
            }

            await submitForm();
          };

          return (
          <ProfilePage>
            <ProfileHeader>
              <div>
                <h1>Mon profil artisan</h1>
              </div>
            </ProfileHeader>

            <StyledForm>
              <ProfileGrid>
                <ProfilePhotoEditor
                  image={values.profileImage}
                  name={[values.firstName, values.lastName].filter(Boolean).join(' ')}
                  role={`Artisan · ${registrationLabel}`}
                  onUploaded={(url) => setFieldValue('profileImage', url)}
                />

                <FormStack>
                  <SectionCard>
                    <SectionHeader>
                      <div>
                        <h2>Identité professionnelle</h2>
                      </div>
                    </SectionHeader>
                    <FieldGrid>
                      <Field name="firstName" label="Prénom" autoComplete="given-name" component={Input} />
                      <Field name="lastName" label="Nom" autoComplete="family-name" component={Input} />
                      <Full><Field type="tel" name="contactNumber" label="Téléphone professionnel" autoComplete="tel" inputMode="tel" component={Input} /></Full>
                      <Full><Field name="address" label="Adresse professionnelle" autoComplete="street-address" component={Input} /></Full>
                    </FieldGrid>
                  </SectionCard>

                  <SectionCard>
                    <SectionHeader>
                      <div>
                        <h2>Activité</h2>
                      </div>
                    </SectionHeader>
                    <FieldGrid>
                      <SelectGroup>
                        <Label htmlFor="specialityType">Spécialité</Label>
                        <Select id="specialityType" name="specialityType" value={values.specialityType} onChange={handleChange}>
                          <option value="" disabled>Choisissez votre spécialité</option>
                          <option value="window">Menuiserie</option>
                          <option value="electricity">Électricité</option>
                        </Select>
                      </SelectGroup>
                      <Full>
                        <SelectGroup>
                          <Label htmlFor="availabilityStatus">Disponibilité</Label>
                          <Select id="availabilityStatus" name="availabilityStatus" value={values.availabilityStatus} onChange={handleChange}>
                            <option value="available">Disponible pour de nouveaux projets</option>
                            <option value="limited">Disponibilité limitée</option>
                            <option value="unavailable">Indisponible temporairement</option>
                          </Select>
                        </SelectGroup>
                      </Full>
                    </FieldGrid>
                  </SectionCard>

                  <SectionCard>
                    <SectionHeader>
                      <div>
                        <h2>Zone d’intervention</h2>
                      </div>
                    </SectionHeader>
                    <GeoSection>
                      <SelectGroup>
                        <Label htmlFor="region">Région principale</Label>
                        <Select
                          id="region"
                          name="region"
                          value={values.region}
                          onChange={(event) => {
                            const nextRegion = normalizeRegionCode(event.target.value);
                            const nextServiceRegions = normalizeRegionList([
                              nextRegion,
                              ...values.serviceRegions.filter((value) => value !== values.region),
                            ]);
                            setFieldValue('region', nextRegion);
                            setFieldValue('serviceRegions', nextServiceRegions);
                          }}
                        >
                          <option value="" disabled>Choisissez votre région</option>
                          {regionOptions.map((region) => (
                            <option key={region.value} value={region.value}>{region.label}</option>
                          ))}
                        </Select>
                        <PasswordHint>
                          {hasServiceLocation
                            ? 'Cette région apparaît sur votre profil. Les distances sont calculées depuis le point de départ confirmé ci-dessous.'
                            : 'Cette région apparaît sur votre profil. Ajoutez ensuite votre point de départ pour activer les distances et les temps de trajet.'}
                        </PasswordHint>
                      </SelectGroup>

                      <AddressLocationPicker
                        id="service-address"
                        label="Point de départ professionnel"
                        value={values.serviceAddress}
                        location={values.serviceLocation}
                        onAddressChange={(next) => setFieldValue('serviceAddress', next, true)}
                        onLocationChange={(next) => {
                          setFieldValue('serviceLocation', next, true);
                          if (!next) setFieldValue('geoNotifications', false);
                        }}
                        placeholder="Atelier, agence ou ville de départ"
                        showMap
                      />
                      <LocationReadiness $ready={hasServiceLocation} role="status">
                        <div>
                          <strong>{hasServiceLocation ? 'Point de départ actif' : 'Point de départ requis pour les distances'}</strong>
                          <span>
                            {hasServiceLocation
                              ? 'Le tri par proximité, les temps de trajet et les alertes locales sont activés après sauvegarde.'
                              : 'Vous pouvez enregistrer le reste du profil, mais aucune distance ne sera affichée tant qu’une adresse n’est pas confirmée.'}
                          </span>
                        </div>
                      </LocationReadiness>

                      <RadiusRow $disabled={!hasServiceLocation}>
                        <div>
                          <Label htmlFor="serviceRadius">Rayon maximal d’intervention</Label>
                          <input
                            id="serviceRadius"
                            name="serviceRadius"
                            type="range"
                            min="5"
                            max="150"
                            step="5"
                            value={values.serviceRadius}
                            onChange={handleChange}
                            disabled={!hasServiceLocation}
                          />
                        </div>
                        <RadiusValue>{values.serviceRadius} km</RadiusValue>
                      </RadiusRow>
                      <GeoOption>
                        <input
                          type="checkbox"
                          name="geoNotifications"
                          checked={hasServiceLocation && values.geoNotifications}
                          onChange={handleChange}
                          disabled={!hasServiceLocation}
                        />
                        <span>Me prévenir lorsqu’un nouveau projet compatible apparaît dans ce rayon.</span>
                      </GeoOption>

                      <MobilityBox>
                        <div>
                          <strong>Déplacements hors de votre région principale</strong>
                          <span>Ne sélectionnez que les zones où vous acceptez réellement des chantiers réguliers.</span>
                        </div>
                        <GeoOption>
                          <input
                            type="checkbox"
                            name="acceptsOutsideRegions"
                            checked={values.acceptsOutsideRegions}
                            onChange={(event) => {
                              const enabled = event.target.checked;
                              setFieldValue('acceptsOutsideRegions', enabled);
                              if (!enabled) {
                                setFieldValue('nationwideService', false);
                                setFieldValue('serviceRegions', [values.region]);
                              }
                            }}
                          />
                          <span>Je peux accepter certains chantiers dans d’autres régions.</span>
                        </GeoOption>

                        {values.acceptsOutsideRegions && (
                          <GeoOption>
                            <input
                              type="checkbox"
                              name="nationwideService"
                              checked={values.nationwideService}
                              onChange={(event) => setFieldValue('nationwideService', event.target.checked)}
                            />
                            <span>Je propose des prestations partout en France.</span>
                          </GeoOption>
                        )}

                        {values.acceptsOutsideRegions && !values.nationwideService && (
                          <RegionGroup>
                            <strong>Autres régions régulières <small>(facultatif)</small></strong>
                            <RegionChecklist aria-label="Autres régions d’intervention régulières">
                              {regionOptions
                                .filter((region) => region.value !== values.region)
                                .map((region) => {
                                  const checked = values.serviceRegions.includes(region.value);
                                  return (
                                    <label key={region.value}>
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => setFieldValue(
                                          'serviceRegions',
                                          normalizeRegionList(checked
                                            ? values.serviceRegions.filter((value) => value !== region.value)
                                            : [...values.serviceRegions, values.region, region.value]),
                                        )}
                                      />
                                      <span>{region.label}</span>
                                    </label>
                                  );
                                })}
                            </RegionChecklist>
                          </RegionGroup>
                        )}
                      </MobilityBox>
                    </GeoSection>
                  </SectionCard>

                  <SectionCard>
                    <SectionHeader>
                      <div>
                        <h2>Vitrine publique</h2>
                      </div>
                    </SectionHeader>
                    <FieldGrid>
                      <Full>
                        <SelectGroup>
                          <Label htmlFor="businessIntroduction">Présentation</Label>
                          <Field
                            as={TextArea}
                            id="businessIntroduction"
                            name="businessIntroduction"
                            maxLength={600}
                            placeholder="Présentez votre expérience, votre façon de travailler et les projets que vous réalisez."
                          />
                          <PasswordHint>{values.businessIntroduction.length}/600 caractères</PasswordHint>
                        </SelectGroup>
                      </Full>
                      <Full>
                        <SelectGroup>
                          <Label htmlFor="certifications">Certifications et qualifications</Label>
                          <Field
                            as={TextArea}
                            id="certifications"
                            name="certifications"
                            placeholder="Une certification par ligne, par exemple : RGE Qualibat"
                          />
                          <PasswordHint>Une certification par ligne. Ne publiez que des qualifications vérifiables.</PasswordHint>
                        </SelectGroup>
                      </Full>
                    </FieldGrid>
                  </SectionCard>

                  <ArtisanDocumentTemplateCard
                    values={values}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                  />

                  <ArtisanCommandCenter profile={firebase.profile} />
                  <InvoicingSetupCard />
                  <StripeConnectPanel />

                  <SectionCard>
                    <ReputationSection profile={firebase.profile} userId={firebase.auth.uid} role="Artisan" />
                  </SectionCard>

                  <SectionCard>
                    <ProfileSecurityCard email={values.email} />
                  </SectionCard>

                  <ProfileLanguageSettings />

                  <Message error show={Boolean(error)}>{error}</Message>

                  <ActionBar $visible={dirty || isSubmitting || loading}>
                    <SaveState $dirty={dirty} $saving={isSubmitting || loading}>
                      <strong>
                        {isSubmitting || loading
                          ? 'Enregistrement en cours…'
                          : dirty && !isValid
                            ? 'Informations à vérifier avant l’enregistrement'
                            : dirty
                              ? 'Modifications non enregistrées'
                              : savedAt
                                ? 'Profil enregistré'
                                : 'Profil à jour'}
                      </strong>
                      <span>
                        {dirty && invalidFieldLabels.length
                          ? `À corriger : ${invalidFieldLabels.slice(0, 3).join(', ')}${invalidFieldLabels.length > 3 ? '…' : ''}`
                          : dirty
                            ? 'Votre logo, votre présentation et vos informations professionnelles seront enregistrés ensemble.'
                            : savedAt
                              ? `Dernière sauvegarde à ${savedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                              : 'Aucune action nécessaire.'}
                      </span>
                    </SaveState>
                    <Button
                      contain
                      type="button"
                      onClick={handleSave}
                      disabled={!dirty || isSubmitting || loading}
                      loading={isSubmitting || loading ? 'Mise à jour…' : null}
                    >
                      {isValid ? 'Enregistrer les modifications' : 'Vérifier puis enregistrer'}
                    </Button>
                  </ActionBar>

                  <DangerCard>
                    <h2>Zone sensible</h2>
                    <p>La suppression du compte est définitive et retire l’accès à vos missions et à votre vitrine.</p>
                    <DeleteButton type="button" onClick={() => setModalOpened(true)}>Supprimer mon compte</DeleteButton>
                  </DangerCard>
                </FormStack>
              </ProfileGrid>
            </StyledForm>
          </ProfilePage>
        );
        }}
      </Formik>

      <Modal opened={modalOpened} close={() => { setModalOpened(false); setDeletePassword(''); }}>
        <ModalContent>
          <h2>Supprimer mon compte</h2>
          <p>Cette action est irréversible. Confirmez-la avec votre mot de passe LPB. Les comptes Google utilisent une confirmation Google sécurisée.</p>
          {(firebase.auth.providerData || []).some((provider) => provider?.providerId === 'password') && (
            <label style={{ display: 'grid', gap: '.45rem', marginBottom: '1rem', fontWeight: 800 }}>
              Mot de passe
              <input
                type="password"
                value={deletePassword}
                onChange={(event) => setDeletePassword(event.target.value)}
                autoComplete="current-password"
                placeholder="Votre mot de passe"
                style={{ minHeight: '3rem', border: '1px solid #d8d1c8', borderRadius: '.8rem', padding: '.75rem .9rem', font: 'inherit' }}
              />
            </label>
          )}
          <ModalActions>
            <Button contain type="button" onClick={() => setModalOpened(false)}>Annuler</Button>
            <Button contain type="button" color="red" onClick={async () => {
                const deleted = await deleteUser(deletePassword);
                if (deleted) {
                  setDeletePassword('');
                  setModalOpened(false);
                }
              }} disabled={loadingDelete} loading={loadingDelete ? 'Suppression…' : null}>
              Supprimer le compte
            </Button>
          </ModalActions>
          <Message error show={Boolean(errorDelete)}>{errorDelete}</Message>
        </ModalContent>
      </Modal>
    </>
  );
};

const GeoSection = styled.div`
  display: grid;
  gap: 1rem;
  min-width: 0;
`;

const LocationReadiness = styled.div`
  display: flex;
  align-items: flex-start;
  gap: .65rem;
  padding: .78rem .85rem;
  border: 1px solid ${({ $ready }) => ($ready ? '#b8dcc7' : '#ead6ad')};
  border-radius: .85rem;
  background: ${({ $ready }) => ($ready ? '#f0faf4' : '#fffaf0')};
  color: ${({ $ready }) => ($ready ? '#245f3f' : '#78591c')};

  > div { display: grid; gap: .14rem; }
  strong { font-size: .8rem; }
  span { font-size: .74rem; line-height: 1.48; }
`;

const RadiusRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0,1fr) auto;
  align-items: end;
  gap: 1rem;
  min-width: 0;
  opacity: ${({ $disabled }) => ($disabled ? .5 : 1)};
  transition: opacity 160ms ease;

  > div { display: grid; gap: .45rem; min-width: 0; }
  input[type='range'] { width: 100%; min-width: 0; accent-color: #c0a375; }

  @media (max-width: 440px) {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
`;

const RadiusValue = styled.strong`
  min-width: 5rem;
  padding: .65rem .8rem;
  border-radius: .8rem;
  background: #f1ece5;
  color: #111;
  text-align: center;

  @media (max-width: 440px) { width: 100%; }
`;

const MobilityBox = styled.div`
  display:grid;gap:.75rem;padding:1rem;border:1px solid #e4ddd2;border-radius:1rem;background:#fff;
  > div:first-child{display:grid;gap:.25rem} > div:first-child span{color:#6f6a63;font-size:.82rem;line-height:1.45}
`;

const RegionGroup = styled.div`
  display: grid;
  gap: .55rem;

  > strong {
    font-size: .78rem;
  }

  small {
    color: #777067;
    font-size: .7rem;
    font-weight: 600;
  }
`;

const RegionChecklist = styled.div`
  display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.45rem;max-height:14rem;overflow:auto;padding:.2rem;
  label{display:flex;align-items:flex-start;gap:.45rem;padding:.55rem;border-radius:.65rem;background:#f7f5f1;font-size:.75rem;line-height:1.3}
  input{margin-top:.1rem;accent-color:#111}
  @media(max-width:760px){grid-template-columns:repeat(2,minmax(0,1fr))}
  @media(max-width:460px){grid-template-columns:1fr}
`;

const GeoOption = styled.label`
  display: flex;
  align-items: flex-start;
  gap: .6rem;
  padding: .85rem;
  border-radius: .9rem;
  background: #f7f5f1;
  color: var(--color-text);
  font-size: .8rem;
  line-height: 1.45;

  input { margin-top: .15rem; accent-color: #000; }
  input:disabled { cursor: not-allowed; }
  &:has(input:disabled) { opacity: .55; }
`;

const mapStateToProps = ({ firebase, auth }) => ({
  firebase,
  loading: auth.profileEdit.loading,
  error: auth.profileEdit.error,
  loadingDelete: auth.deleteUser.loading,
  errorDelete: auth.deleteUser.error,
});

export default connect(mapStateToProps, {
  editProfile: actions.editAccountArtisan,
  cleanUp: actions.cleanMessage,
  deleteUser: actions.deleteUser,
})(ProfileArtisan);
